import React from 'react';
import ReactDOM from 'react-dom/client';
// FIRST of all: the brand (name, languages, calendar, market) from env.js —
// everything imported below may read it while it loads.
import './src/lib/brandBoot.js';
// Then, before any screen: points the shared clinical module (visit room,
// patient record) at the doctor BFF with this panel's sign-in
// (docs/CLINICAL_KIT_DESIGN.md). Import order is evaluation order.
import './src/lib/clinicalHost.js';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { ToastProvider } from '@parsina/framework';
import {
  getToken, getRefresh, isTokenExpired,
  handleCallback, redirectToLogin, refreshAccessToken,
  clearRedirectGuard, parseJwt,
} from './src/lib/auth.js';
// No bffQuery here any more: fetching «کلینیک من» moved into ClinicContext, which
// has to load the membership list anyway and would otherwise ask twice.
import { ShortcutProvider } from './src/lib/shortcuts.jsx';
import { I18nProvider, useTranslation } from './src/i18n';
import { useMyProfile } from './src/hooks/useMyProfile.js';
import ClinicLayout from './ClinicLayout.jsx';
import { ClinicContextProvider, useClinicContext, BOOT } from './src/context/ClinicContext.jsx';
import ClinicGate from './src/components/common/ClinicGate.jsx';
import StartRedirect from './src/components/common/StartRedirect.jsx';
import StartScreen from './src/screens/Start';
import OnboardingScreen from './src/screens/Onboarding/Onboarding.web.jsx';
import { BootLoading, BootError } from './src/components/auth/BootScreen.jsx';
import InvitationsScreen from './src/screens/Invitations';
import { stripClinic } from './src/hooks/useAppNavigate.js';
import { isGoodsView } from './src/constants/moduleViews.js';
import { skeletonForPath } from './src/screens/skeletons.jsx';
import { lazyWithReload } from './src/lib/lazyWithReload.js';
import { initPwa } from './src/lib/pwa.js';
import { registerServiceWorker } from './src/lib/registerSw.js';
/* ── Route-level code splitting ──────────────────────────────────────────────
   Every screen used to be a static import, so the first bundle carried all of
   them: the 2200-line cash desk, the whole operations module with its four
   drawers, and the imaging dependencies — for a receptionist who opens one
   screen and stays on it.

   That is a desktop-sized bill charged to a phone. The clinic app now has a
   mobile shell, and the people using it are on a handset on a clinic's wifi,
   paying for the cash desk on the way to the day list.

   `Suspense` sits INSIDE the shell (below), so a chunk arriving late shows the
   destination page's own skeleton where the screen goes rather than blanking
   the navigation the operator is using to get there. `Invite` stays eager: it is the pre-auth
   landing for a QR scan, and a person standing at a clinic desk with a
   just-scanned code should not wait for a second round trip.

   `lazyWithReload`, not React.lazy: a tab opened before a deploy asks for the
   previous build's chunk names, and reloading once fetches the current ones. */
const ClinicScreen = lazyWithReload(() => import('./src/screens/Clinic'));
const ReceptionScreen = lazyWithReload(() => import('./src/screens/Reception'));
const PatientsScreen = lazyWithReload(() => import('./src/screens/Patients'));
const SettingsScreen = lazyWithReload(() => import('./src/screens/Settings'));
const AddPatientScreen = lazyWithReload(() => import('./src/screens/AddPatient'));
const OperationsScreen = lazyWithReload(() => import('./src/screens/Operations'));
const GoodsScreen = lazyWithReload(() => import('./src/screens/Goods'));
const StoreScreen = lazyWithReload(() => import('./src/screens/Store'));
const ReportsScreen = lazyWithReload(() => import('./src/screens/Reports'));
const AccountingScreen = lazyWithReload(() => import('./src/screens/Accounting'));
const PeopleScreen = lazyWithReload(() => import('./src/screens/People'));
const RoleEditorScreen = lazyWithReload(() => import('./src/screens/People/RoleEditor.jsx'));
// The shared clinical screens — the heaviest code the panel can load, and only a
// practitioner of the clinic ever does, so they are their own chunks.
const ClinicalVisitRoute = lazyWithReload(() => import('./src/screens/Clinical/VisitRoute.jsx'));
const ClinicalRecordRoute = lazyWithReload(() => import('./src/screens/Clinical/RecordRoute.jsx'));
// «اتاق خدمت» and «کارتابل خدمات» (PM-0693) — the room any booked performer opens.
const ServiceRoomRoute = lazyWithReload(() => import('./src/screens/Clinical/ServiceRoomRoute.jsx'));
const ServiceWorklistScreen = lazyWithReload(() => import('./src/screens/ServiceWorklist'));

import InviteScreen from './src/screens/Invite';

import './styles.css';

// Which paths render inside ClinicLayout — i.e. get the sidebar. A path missing
// here still routes and still renders; it just loses the whole shell, silently.
// That is how «عملیات و هزینه» shipped sidebar-less: the merge added /operations
// to the route table and to ROUTES, and this list was left behind. The legacy
// /inventory, /expenses and /services entries stay so their redirect frame keeps
// the shell too — otherwise the sidebar blinks out on the way through, and so
// does /goods, for the same reason and with the same failure mode if forgotten.
const WEB_LAYOUT_PREFIXES = ['/reception', '/my-services', '/patients', '/operations', '/goods', '/store', '/inventory', '/reports', '/accounting', '/expenses', '/about', '/doctors', '/services', '/people', '/subscription', '/clinic', '/settings'];

/**
 * `/subscription` and `/about` → «کلینیک من», with the query string intact.
 *
 * Forwarding the query is not tidiness. A checkout started before this merge
 * shipped has `${origin}/subscription` stored as its return URL inside the
 * payment record, and the payment BFF appends `?payment=success&ref_id=…` to
 * whatever that says. Dropping the query on the way through would land a clinic
 * that has just paid on a page with no idea it happened — no toast, no refresh,
 * and a status that still reads «منقضی شده».
 */
const MergedClinicRedirect = ({ view }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('view', view);
  return <Navigate to={{ pathname: '/clinic', search: `?${params.toString()}` }} replace />;
};

/**
 * `/clinic/:view` → `/clinic?view=:view`.
 *
 * The BFF hands out `renewUrl: '/clinic/billing'` in every entitlement error, so
 * that path has to resolve to something. An unknown segment falls through to the
 * page's own normalisation rather than 404-ing.
 */
/**
 * `/payroll?y=&m=` → `/people?tab=payroll&y=&m=`.
 *
 * A `<Navigate>` with a literal search would drop the month, which is the one
 * thing a payroll bookmark is FOR. The two parameter names are unchanged across
 * the move (`People/payroll.js` owns them for both halves), so carrying the query
 * through verbatim is the whole redirect.
 */
const PayrollRedirect = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('tab', 'payroll');
  return <Navigate to={{ pathname: '../people', search: `?${params.toString()}` }} replace />;
};

const ClinicViewAlias = () => {
  const { view } = useParams();
  return <Navigate to={`/clinic?view=${encodeURIComponent(view || '')}`} replace />;
};

/**
 * `/operations` — «مدیریت هزینه‌ها», or a forward to «کالاها و خدمات».
 *
 * Eleven of the merged module's sixteen `?view=` values moved to `/goods` on
 * 2026-09-12, and there are years of links at the old address: bookmarks, links
 * pasted into messages, the reception dashboard's alert tiles, the report
 * drill-downs. Each one has to land on the view it named, so this asks the rail
 * which page owns the requested view and forwards the ones that left.
 *
 * Three things it does deliberately:
 *
 *  1. **The whole query string travels.** `?view=services&service=<id>` arrives
 *     at `/goods` intact, so a later fix to that deep link needs no second
 *     redirect and no second place to remember it.
 *  2. **It is relative** (`../goods`), like the legacy redirects beside it: an
 *     absolute `/goods` leaves the clinic, and `LegacyPathRedirect` would then
 *     re-enter through the person's DEFAULT clinic — so a link opened at the
 *     Isfahan branch would quietly land in Tehran.
 *  3. **It runs before the screen's chunk.** Returning early means the
 *     `OperationsScreen` lazy component is never rendered and therefore never
 *     fetched, so an old `?view=items` link downloads one chunk, not two.
 *
 * A view the rail does not know (a typo, a retired key) is NOT forwarded — the
 * expenses page normalises it to the first entry that person may open, which is
 * the behaviour a bad `?view=` has always had.
 */
const OperationsOrGoods = () => {
  const location = useLocation();
  const requested = new URLSearchParams(location.search).get('view');
  if (isGoodsView(requested)) {
    return <Navigate to={{ pathname: '../goods', search: location.search }} replace />;
  }
  return <ClinicGate><OperationsScreen /></ClinicGate>;
};

/**
 * Adopt the language stored on the signed-in user.
 *
 * The panel has already painted — the pre-paint script in index.html and
 * I18nProvider both read the localStorage cache, so the first frame is in the
 * right language and direction. This only catches the case the cache cannot:
 * a staff member who chose English at one workstation and is now at another,
 * or one whose cache was cleared. `setLocale` ignores an empty or unshipped
 * tag, so a null `language` (never chosen) leaves the resolution order alone.
 *
 * Renders nothing: it exists so the effect sits inside the authed tree, where
 * `myProfile` is actually answerable, without wrapping the shell in a layer.
 */
const LanguageSync = () => {
  const { setLocale } = useTranslation();
  const { profile } = useMyProfile();
  const language = profile?.language;

  React.useEffect(() => {
    if (language) setLocale(language);
  }, [language, setLocale]);

  return null;
};

/**
 * The clinic-scoped route table.
 *
 * Rendered under `/c/:clinicRef/*`, so every path here is relative — react-router
 * resolves them against the prefix and the tenant never has to be threaded through
 * a single one of them.
 *
 * The legacy redirects are relative too, which is what keeps them inside the
 * clinic: `<Navigate to="../operations?view=items">` from `/c/mehr/inventory` lands
 * on `/c/mehr/operations?view=items`. An absolute `/operations` would have dropped
 * the person out of their clinic on the way through a redirect they never saw.
 */
const ClinicRoutes = () => (
  <Routes>
    <Route path="/" element={<ClinicGate><span /></ClinicGate>} />
    <Route path="reception" element={<ClinicGate><ReceptionScreen /></ClinicGate>} />
    {/* Legacy: پذیرش was «داشبورد» at /dashboard until 2026-08. Kept so existing
        bookmarks and any already-loaded tab still land on the page rather than
        bouncing through the '*' catch-all. */}
    <Route path="dashboard" element={<Navigate to="../reception" replace />} />
    <Route path="patients" element={<ClinicGate><PatientsScreen /></ClinicGate>} />
    <Route path="patients/new" element={<ClinicGate><AddPatientScreen /></ClinicGate>} />
    {/* «سوابق بیمار» and the visit room — the shared clinical module, for a
        practitioner of this clinic (ClinicalFrame gates it). The record sits under
        /patients so ROUTE_PERMISSIONS asks for patients.view as well. */}
    <Route path="patients/:patientId/record/:tab?" element={<ClinicGate><ClinicalRecordRoute /></ClinicGate>} />
    <Route path="visit/:patientId" element={<ClinicGate><ClinicalVisitRoute /></ClinicGate>} />
    {/* «اتاق خدمت» — whoever the desk booked to perform a service; the clinic BFF
        decides per appointment (PM-0693). */}
    <Route path="service/:appointmentId" element={<ClinicGate><ServiceRoomRoute /></ClinicGate>} />
    <Route path="my-services" element={<ClinicGate><ServiceWorklistScreen /></ClinicGate>} />
    {/* ── مدیریت هزینه‌ها + کالاها و خدمات — the two halves ─────────────
        «عملیات و هزینه» merged three screens in 2026-07 and grew to sixteen
        rail destinations answering two different questions: what the clinic
        SPENT, and what it STOCKS AND OFFERS. On 2026-09-12 it split back into
        two pages (docs/CLINIC_EXPENSES_AND_GOODS_SPLIT_DESIGN.md).

        `/operations` KEPT ITS PATH and changed only its label, because the
        bank's Sepehr return URL, the reports resolver's attention links and
        years of bookmarks all name it; renaming it would have bought a prettier
        URL at the price of a redirect that must exist forever and a BFF deploy
        ordered against the client's. `OperationsOrGoods` forwards the views
        that left. The legacy paths now point at whichever page absorbed them,
        so `/inventory` still reaches the shelf in ONE hop rather than two. */}
    <Route path="operations" element={<OperationsOrGoods />} />
    <Route path="goods" element={<ClinicGate><GoodsScreen /></ClinicGate>} />
    <Route path="store" element={<ClinicGate><StoreScreen /></ClinicGate>} />
    <Route path="inventory" element={<Navigate to="../goods?view=items" replace />} />
    <Route path="reports" element={<ClinicGate><ReportsScreen /></ClinicGate>} />
    <Route path="accounting" element={<ClinicGate><AccountingScreen /></ClinicGate>} />
    <Route path="expenses" element={<Navigate to="../operations?view=expenses" replace />} />
    <Route path="about" element={<MergedClinicRedirect view="connect" />} />
    <Route path="services" element={<Navigate to="../goods?view=services" replace />} />
    {/* ── همکاران — the merged module ────────────────────────────
        «پزشکان» and «پرسنل» were one concept (Person → Membership → Role)
        wearing two interfaces; /people is now both. /doctors redirects INTO
        it filtered to practitioners, so bookmarks and muscle memory land on
        the same people they always did. «تقویم و نوبت‌دهی» — the practitioner's
        week — was removed from the panel on 2026-09-13; the path stays
        answerable and lands on پذیرش, which is where the desk works. */}
    <Route path="calendar" element={<Navigate to="../reception" replace />} />
    <Route path="doctors" element={<Navigate to="../people?kind=practitioner" replace />} />
    <Route path="people" element={<ClinicGate><PeopleScreen /></ClinicGate>} />
    <Route path="people/roles/:roleId" element={<ClinicGate><RoleEditorScreen /></ClinicGate>} />
    {/* «حقوق و دستمزد» stopped being a page on 2026-09-13. The OPERATION is a tab
        of «همکاران» — it is something you do to the people already on that screen,
        from contracts written in their own drawer — and the accountant's reading of
        it is a view of «حسابداری». The old path redirects into the operation
        CARRYING ITS MONTH, so a bookmarked `?y=&m=` opens the month it named rather
        than today's. */}
    <Route path="payroll" element={<PayrollRedirect />} />
    {/* ── کلینیک من — the merged module ──────────────────────────────
        «اشتراک کلینیک» and «معرفی کلینیک» were one subject wearing two
        interfaces; /clinic is both, switched by ?view=. The old paths redirect
        INTO it with the matching view preselected, so bookmarks, the shell's
        paywall banner and any in-flight payment's stored return URL all land
        where the person expected. */}
    <Route path="settings" element={<ClinicGate><SettingsScreen /></ClinicGate>} />
    <Route path="clinic" element={<ClinicGate><ClinicScreen /></ClinicGate>} />
    <Route path="clinic/:view" element={<ClinicViewAlias />} />
    <Route path="subscription" element={<MergedClinicRedirect view="billing" />} />
    <Route path="*" element={<ClinicGate><span /></ClinicGate>} />
  </Routes>
);

/**
 * Legacy un-prefixed paths → the same path inside the person's default clinic.
 *
 * Every bookmark, every link in a message and every muscle-memory URL predates the
 * `/c/:ref` prefix, and there are years of them. They must not land on «شروع» —
 * that would read as "you have been removed from your clinic" to someone whose only
 * mistake was having a bookmark.
 */
const LegacyPathRedirect = () => {
  const location = useLocation();
  const { defaultRef } = useClinicContext();
  if (!defaultRef) return <Navigate to="/start" replace />;
  return <Navigate to={`/c/${defaultRef}${location.pathname}${location.search}`} replace />;
};

/**
 * The router, once memberships are known.
 *
 * FIVE boot outcomes, not two. «The query failed» and «this person genuinely has no
 * clinic» used to collapse into hasClinic=false, whose only rendering is the
 * onboarding wizard — so a database blip told a clinic owner of three years to go
 * create their clinic. That reasoning extends verbatim to the two states this
 * design adds: «waiting on an invitation» and «belongs to several» are answers the
 * BFF actually gave, and neither may be rendered as a failure or as an emptiness.
 */
const RoutedApp = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { state, defaultRef, error, reload, activeRef } = useClinicContext();

  const barePath = stripClinic(location.pathname);
  const inLayout = Boolean(activeRef) && (
    WEB_LAYOUT_PREFIXES.some((p) => barePath.startsWith(p)) || barePath === '/'
  );

  // The SAME card the sign-in check showed, continued rather than replaced:
  // the doctor panel keeps one card up for its whole boot, and so does this.
  if (state === BOOT.LOADING) {
    return <BootLoading onManualSignIn={() => redirectToLogin()} testId="boot-loading" />;
  }

  // The backend could not answer. Say so and offer a retry — never the wizard,
  // which would invite a duplicate clinic for someone who already has one.
  if (state === BOOT.ERROR) {
    return (
      <BootError
        tone="warning"
        testId="boot-error"
        stateTestId="boot-error-state"
        title={t('errors.network.noServer')}
        body={t('errors.clinic.unavailable')}
        detail={error || undefined}
        onAction={() => reload()}
      />
    );
  }

  // Nothing to enter. «شروع» is the whole app here: creating a clinic, entering a
  // code, and any invitation waiting are all reachable from it, so it is not a dead
  // end even though it is not a panel. The catch-all remembers a landing-site
  // «خرید اشتراک» on its way past, so onboarding ends on «اشتراک» (StartRedirect).
  if (state === BOOT.NONE || state === BOOT.PENDING_ONLY) {
    return (
      <Routes>
        <Route path="/start" element={<StartScreen />} />
        <Route path="/onboarding" element={<OnboardingScreen />} />
        <Route path="/invitations" element={<Navigate to="/start" replace />} />
        <Route path="*" element={<StartRedirect />} />
      </Routes>
    );
  }

  const routes = (
    <Routes>
      <Route path="/start" element={<StartScreen />} />
      <Route path="/onboarding" element={<OnboardingScreen />} />
      <Route path="/invitations" element={<InvitationsScreen />} />
      <Route path="/c/:clinicRef/*" element={<ClinicRoutes />} />
      <Route path="/" element={<Navigate to={`/c/${defaultRef}/`} replace />} />
      {/* Every pre-prefix path, forwarded into the person's default clinic. */}
      <Route path="*" element={<LegacyPathRedirect />} />
    </Routes>
  );

  // Remounted on the clinic ref. This ONE line is what makes «no stale row survives
  // a switch» structural rather than a review checklist: every screen, filter,
  // drawer and cached list belonging to the previous clinic is thrown away, because
  // the alternative is trusting sixteen screens to each remember to clear themselves.
  // The boundary for the lazy route chunks. It sits here — INSIDE the fragment
  // that ClinicLayout wraps — so that a chunk still arriving leaves the shell
  // painted: the sidebar, or on a phone the bottom navigation, stays on screen
  // and stays operable. A Suspense placed around the layout instead would blank
  // the very navigation the operator just used, which reads as the app having
  // crashed rather than as a screen loading.
  const keyed = (
    <React.Fragment key={activeRef || 'none'}>
      <React.Suspense fallback={<ScreenFallback />}>{routes}</React.Suspense>
    </React.Fragment>
  );

  // Outside the layout — including `/onboarding` once the clinic exists, i.e.
  // the roles step — the screen sits in `.bare-screen`, sized in styles.css by
  // the SMALL viewport. It was `height: 100vh` here, which on iOS is the large
  // one: its bottom sat under Safari's toolbar, and «ساخت نقش‌ها و ورود» with it.
  return inLayout
    ? <ClinicLayout>{keyed}</ClinicLayout>
    : <div className="bare-screen">{keyed}</div>;
};

/**
 * What a route shows while its chunk is still in flight.
 *
 * The DESTINATION page's own skeleton, chosen from the URL (`skeletons.jsx`),
 * so the transition draws the shape of the page that is coming — its header,
 * its filters, its table — and the data fills it in place. Not a spinner: the
 * shell around this is already painted, and a spinner where a page should be
 * is a small blank moment in which the panel forgets which page it is on
 * (DESIGN.md §"Loading & skeleton" — navigation). It fills its container so
 * nothing collapses and jumps when the real content arrives.
 *
 * The pre-membership routes (`/start`, `/onboarding`, `/invitations`) are
 * eager imports and never suspend; the bare `role="status"` box is only for a
 * path this app has no page for, which the gate turns into «not found» next.
 */
const ScreenFallback = () => {
  const location = useLocation();
  const { t } = useTranslation();
  return (
    // `height`, not `flex` — the shell's page-content column is a block with
    // `overflow-y: auto`, not a flex container, so `flex: 1` here resolved to
    // nothing and this box fell back to its content height. A skeleton inside
    // it then measured `height: 100%` against an auto-height parent, which CSS
    // ignores, so every route skeleton collapsed to the height of its own
    // blocks while the real screen — a direct child of that column — filled.
    // That is the one difference between the two, and it has to be zero: the
    // fallback IS the shape of the page that is coming.
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }} data-testid="screen-fallback">
      {skeletonForPath(location.pathname, location.search) || (
        <div role="status" aria-busy="true" aria-label={t('common.state.loading')} data-state="loading" style={{ flex: 1 }} />
      )}
    </div>
  );
};

const AppShell = () => {
  const location = useLocation();

  // `/invite/:code` is answerable before anything is known about membership — it is
  // how a person with no clinic at all arrives — so it is routed OUTSIDE the
  // provider's boot gate rather than behind it.
  if (location.pathname.startsWith('/invite/')) {
    return (
      <Routes>
        <Route path="/invite/:code" element={<InviteScreen />} />
      </Routes>
    );
  }

  return (
    <ClinicContextProvider>
      <RoutedApp />
    </ClinicContextProvider>
  );
};

const App = () => {
  const { t } = useTranslation();
  const [authReady, setAuthReady] = React.useState(false);
  const [authError, setAuthError] = React.useState(null);
  const [bootOffline, setBootOffline] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      const urlParams = new URLSearchParams(window.location.search);

      if (urlParams.get('auth_error')) {
        window.history.replaceState({}, '', '/');
        setAuthError(t('errors.auth.serviceUnreachable'));
        return;
      }

      // Every sign-in below is a full navigation to Keycloak. As an installed
      // app (docs/PWA.md) the service worker can boot this shell with no
      // network, and that navigation would replace the window the person just
      // opened from the taskbar with the browser's own connection-error page.
      // Say what happened instead, and offer the one action that helps.
      const signIn = async () => {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          setBootOffline(true);
          return;
        }
        await redirectToLogin();
      };

      if (urlParams.has('code')) {
        const ok = await handleCallback();
        if (!ok) { await signIn(); return; }
      }

      let token = getToken();
      // A new tab or a reopened browser has no access token but may hold the
      // 30-day refresh token: sign in silently from it first (PM-0158).
      if (!token && getRefresh() && await refreshAccessToken()) token = getToken();
      if (!token) { await signIn(); return; }

      if (isTokenExpired(token)) {
        const refreshed = getRefresh() ? await refreshAccessToken() : false;
        if (!refreshed) { await signIn(); return; }
        token = getToken();
      }

      parseJwt(token);
      clearRedirectGuard();
      setAuthReady(true);
    })();
  }, []);

  // Both states are the doctor panel's boot card (BootScreen.jsx), which is
  // the reference for how signing in looks in every Parsina panel.
  if (bootOffline) {
    return (
      <BootError
        tone="warning"
        testId="boot-offline"
        title={t('errors.auth.offlineTitle')}
        body={t('errors.auth.offlineBody')}
        onAction={() => { window.location.reload(); }}
      />
    );
  }

  if (authError) {
    return (
      <BootError
        testId="auth-error"
        stateTestId="auth-error-state"
        title={t('errors.auth.title')}
        body={authError}
        onAction={() => { window.location.href = '/'; }}
      />
    );
  }

  if (!authReady) {
    return <BootLoading onManualSignIn={() => redirectToLogin()} />;
  }

  return (
    <ToastProvider>
      <BrowserRouter>
        {/* Inside the router so a shortcut can navigate, and above the shell so the
            `?` overlay and any global key outlive a route change. */}
        <ShortcutProvider>
          {/* Above the shell, so the language is adopted once for the session
              rather than re-queried by whichever screen happens to be open. */}
          <LanguageSync />
          <AppShell />
        </ShortcutProvider>
      </BrowserRouter>
    </ToastProvider>
  );
};

// Before the first render: capture `beforeinstallprompt` (Edge/Chrome on
// Windows can fire it while React is still mounting), take jump-list launches
// into this window, and register the service worker (docs/PWA.md).
initPwa();
registerServiceWorker();

// I18nProvider is outermost, ABOVE ToastProvider and App, because everything
// below it reads its copy through `t` — including the auth and onboarding
// screens, which render long before `myProfile` resolves. It takes the language
// from the same localStorage cache the pre-paint script in index.html used, so
// `dir` never changes between the first frame and the first render, and the
// server's copy is adopted later by <LanguageSync/> without a reload.
ReactDOM.createRoot(document.getElementById('root')).render(
  <I18nProvider>
    <App />
  </I18nProvider>
);
